import React from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useChat } from "../../shared/contexts/ChatContext";
import { useLocationHandling } from "../pages/Location/useLocationHandling";
import api from "../../shared/api/axios";
import HighLogo from "./HighLogo";
import logo from "../../assets/logo.png";

async function getLocationConsent(): Promise<boolean> {
  try {
    const { data } = await api.get('/privacy/preferences');
    const ok = !!data?.locationConsent;
    localStorage.setItem('privacy:locationConsent', String(ok));
    return ok;
  } catch {
    return false; // safest fallback
  }
}


const ROUTES = {
  Gallery: "/gallery",
  Favorite: "/following",
  MyLikes: "/mylikes",
  UploadPost: "/gallery-posting",
  Notifications: "/notifications",
  MyProfile: "/profile",
  MyPosts: "/my-posts",
  Settings: "/settings/overview",
  Login: "/login",
};

const MenuMobile = () => {

  const { activeChatUserId } = useChat();

  const navigate = useNavigate();
  const { t } = useTranslation();


  const go = (routeName: string) => {
    navigate(routeName);
  };


const handleLocationChange = async () => {
  const consent = await getLocationConsent();

  console.log("🧭 Location consent from backend:", consent);
  console.log("🧠 Cached localStorage:", localStorage.getItem("privacy:locationConsent"));

  if (consent) {
    navigate("/change-location-geo");
  } else {
    navigate("/change-location-nogeo");
  }
};


  const handleLogout = () => {
    // Handle logout logic here (e.g., clear session, token)
    navigate(ROUTES.Login);
  };
const navigateToActiveChat = () => {
    if (activeChatUserId) {
      navigate(`/activechat/${activeChatUserId}`);
    } else {
      navigate("/activechat"); // fallback
    }
  };
  return (
    <div style={styles.screen}>
      {/* Header */}
      <div style={styles.header}>
  <button
    type="button"
    onClick={() => navigate("/gallery")}
    style={styles.logoBtn}
    aria-label="Go to gallery"
  >
    <HighLogo
      src={logo}
      style={styles.logo}
      padding={0}
    />
  </button>

  <span style={styles.headerTitle}>{t("openMenu.title")}</span>

  <button onClick={() => navigate(-1)} style={styles.closeBtn}>
    <FaTimes size={18} color="#8d8f95" />
  </button>
</div>

      <div style={styles.list}>
        <MenuButton label={t("openMenu.gallery")} onClick={() => go(ROUTES.Gallery)} />
        <MenuButton label={t("openMenu.favorite")} onClick={() => go(ROUTES.Favorite)} />
        <MenuButton label={t("openMenu.myLikes")} onClick={() => go(ROUTES.MyLikes)} />
        <MenuButton label={t("openMenu.locationChange")} onClick={handleLocationChange} />
        <MenuButton label={t("openMenu.uploadPost")} onClick={() => go(ROUTES.UploadPost)} />
        <MenuButton label={t("openMenu.notifications")} onClick={() => go(ROUTES.Notifications)} />
        <MenuButton label={t("openMenu.myProfile")} onClick={() => go(ROUTES.MyProfile)} />
        <MenuButton label={t("openMenu.myPosts")} onClick={() => go(ROUTES.MyPosts)} />
        
        {/* Ensure activeChatUserId is passed to navigate to chat */}
        <MenuButton
          label={t("openMenu.chat")}
          onClick={navigateToActiveChat}
        />

        <MenuButton label={t("openMenu.settings")} onClick={() => go(ROUTES.Settings)} />
    
        <div style={styles.divider}></div>

        <MenuButton label={t("openMenu.logout")} danger onClick={handleLogout} />
      </div>
    </div>
  );
};

function MenuButton({
  label,
  onClick,
  danger,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <div onClick={onClick} style={{ ...styles.item, ...(danger && styles.itemDanger) }}>
      <span style={{ ...styles.itemText, ...(danger && styles.itemTextDanger) }}>{label}</span>
      <span style={{ ...styles.itemChevron, ...(danger && styles.itemTextDanger) }}>›</span>
    </div>
  );
}

const styles = {
  screen: {
    flex: 1,
    backgroundColor: "#0b0c10",
    maxWidth: "400px",
    margin: "0 auto",
  } as React.CSSProperties,

  header: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "54px",
    marginTop: '-9px',
    paddingBottom: "21px",
  } as React.CSSProperties,

  logoBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    zIndex: 1,
  } as React.CSSProperties,

  logo: {
    width: 92,
    marginTop: '10px',
    marginLeft: '-25px',
    display: "block",
    height: "auto",
  } as React.CSSProperties,

  headerTitle: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    color: "#babcc2",
    fontSize: "20px",
    fontWeight: "700",
    textAlign: "center",
    pointerEvents: "none",
  } as React.CSSProperties,

  closeBtn: {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  } as React.CSSProperties,

  list: {
    padding: "0 12px",
  } as React.CSSProperties,

  item: {
    minHeight: "24px",
    borderRadius: "10px",
    padding: "12px",
    backgroundColor: "#12141a",
    border: "1px solid #1b1f28",
    marginVertical: "6px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    marginBottom: "4px",
  } as React.CSSProperties,

  itemPressed: {
    backgroundColor: "#151923",
    borderColor: "rgb(56, 6, 117)",
  } as React.CSSProperties,

  itemDanger: {
    backgroundColor: "#171417",
    borderColor: "rgba(185,28,28,0.35)",
  } as React.CSSProperties,

  itemText: {
    color: "#babcc2",
    fontSize: "16px",
    fontWeight: "600",
  } as React.CSSProperties,

  itemTextDanger: {
    color: "#fca5a5",
  } as React.CSSProperties,

  itemChevron: {
    color: "#babcc2",
    fontSize: "18px",
  } as React.CSSProperties,

  divider: {
    height: "1px",
    backgroundColor: "#1f2430",
    marginVertical: "8px",
  } as React.CSSProperties,
};
export default MenuMobile;
